<template>
  <div class="datePicker input-group" :qaid="qaid">
    <span v-if="allowIcon" class="input-group-label">
      <i class="material-icons">{{ enableTime ? 'access_time' : 'date_range' }}</i>
    </span>
    <input
      :id="inputId"
      ref="picker"
      class="datePicker__input form-control flatpickr"
      type="text"
      :name="name"
      :placeholder="placeholder"
      data-input
    />
  </div>
</template>

<script>
import Flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';

const includedEvents = ['onChange', 'onClose', 'onDestroy', 'onKeyDown', 'onMonthChange', 'onOpen', 'onYearChange'];
const excludedEvents = ['onValueUpdate', 'onDayCreate', 'onParseConfig', 'onReady', 'onPreCalendarPosition'];

const allEvents = includedEvents.concat(excludedEvents);
const configCallbacks = ['locale', 'showMonths'];
const camelToKebab = (string) => {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};
const arrayify = (obj) => {
  return obj instanceof Array ? obj : [obj];
};

export default {
  props: {
    allowIcon: {
      type: Boolean,
      default: true,
    },
    allowInput: {
      type: Boolean,
      default: false,
    },
    allowInvalidPreload: {
      type: Boolean,
      default: false,
    },
    altFormat: {
      type: String,
      default: 'M j, Y',
    },
    altInput: {
      type: Boolean,
      default: true,
    },
    /* eslint-disable-next-line vue/prop-name-casing */
    AppendTo: {
      type: String,
      default: null,
    },
    dateFormat: {
      type: String,
      default: 'Y-m-d',
    },
    defaultDate: {
      type: [Date, String, Array],
      default: null,
    },
    enableTime: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => includedEvents,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    inputId: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: [Date, String],
      default: null,
    },
    minDate: {
      type: [Date, String],
      default: null,
    },
    minuteIncrement: {
      type: Number,
      default: 30,
    },
    mode: {
      type: String,
      default: 'single',
    },
    name: {
      type: String,
      default: '',
    },
    nextArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2z"/></svg>`,
    },
    noCalendar: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    prevArrow: {
      type: String,
      default: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z"/></svg>`,
    },
    qaid: {
      type: String,
      default: 'date-picker',
    },
    value: {
      default: null,
      validator(value) {
        return (
          value === null ||
          value instanceof Date ||
          typeof value === 'string' ||
          value instanceof String ||
          value instanceof Array ||
          typeof value === 'number'
        );
      },
    },
  },
  data() {
    return {
      // The FlatPicker instance
      fp: null,
      config: {
        mode: this.mode,
        allowInput: this.allowInput,
        allowInvalidPreload: this.allowInvalidPreload,
        inline: this.inline,
        altInput: this.altInput,
        altFormat: this.altFormat,
        dateFormat: this.dateFormat,
        appendTo: this.appendTo,
        defaultDate: this.normalizeDate(this.defaultDate),
        nextArrow: this.nextArrow,
        prevArrow: this.prevArrow,
        enableTime: this.enableTime,
        noCalendar: this.noCalendar,
        minuteIncrement: this.minuteIncrement,
        minDate: this.normalizeDate(this.minDate),
        maxDate: this.normalizeDate(this.maxDate),
        onChange: this.onChange,
      },
    };
  },
  watch: {
    config: {
      deep: true,
      handler(newConfig) {
        const config = {...newConfig};

        // Workaround: Don't pass hooks to configs again otherwise
        // previously registered hooks will stop working
        // Notice: we are looping through all events
        // This also means that new callbacks can not passed once component has been initialized
        allEvents.forEach((hook) => {
          delete config[hook];
        });

        this.fp.set(config);

        // Workaround: Allow to change locale dynamically
        configCallbacks.forEach((name) => {
          if (typeof config[name] !== 'undefined') {
            this.fp.set(name, config[name]);
          }
        });
      },
    },

    value(newValue) {
      if (newValue === this.$el.value) return;

      this.fp && this.fp.setDate(newValue, true);
    },
    isDisabled: function(newValue) {
      this.$refs.picker.disabled = newValue;
      if (!newValue) {
        this.setPickr();
      } else {
        this.$refs.picker.value = null;
        this.fpInput().removeEventListener('blur', this.onBlur);
        this.fp.destroy();
        this.fp = null;
      }
    },
  },
  mounted() {
    if (this.fp) return;
    if (this.isDisabled) {
      this.$refs.picker.disabled = this.isDisabled;
    } else {
      this.setPickr();
    }
  },

  beforeDestroy() {
    if (this.fp) {
      this.fpInput().removeEventListener('blur', this.onBlur);
      this.fp.destroy();
      this.fp = null;
    }
  },
  methods: {
    setPickr() {
      const config = {...this.config};

      this.events.forEach((hook) => {
        config[hook] = arrayify(config[hook] || []).concat((...args) => {
          this.$emit(camelToKebab(hook), ...args);
        });
      });

      config.defaultDate = this.normalizeDate(this.value || config.defaultDate);
      this.fp = new Flatpickr(this.$refs.picker, config);

      this.fpInput().addEventListener('blur', this.onBlur);
    },
    onChange(selectedDates, dateStr) {
      const [startDate, endDate] = selectedDates;
      if (this.mode === 'range') {
        this.$emit('change', {
          event: dateStr,
          startDate,
          endDate,
        });
      } else {
        this.$emit('change', {
          event: dateStr,
          selectedDate: startDate,
        });
      }
    },
    normalizeDate(date) {
      if (date instanceof Date && !this.enableTime) {
        return new Date(this.minDate).toISOString().slice(0, 10);
      } else {
        return date;
      }
    },
    fpInput() {
      return this.fp.altInput || this.fp.input;
    },

    onBlur(event) {
      this.$emit('blur', event.target.value);
    },
  },
};
</script>
